import jwtDecode from 'jwt-decode';

/**
 * Parse user role from JWT token
 * @param {string} idToken - JWT ID token from Cognito
 * @returns {string|null} - User role ('librarian' or 'reader') or null if not found
 */
export const parseUserRole = (idToken) => {
  if (!idToken || typeof idToken !== 'string') {
    console.warn('parseUserRole: Invalid or missing token, defaulting to reader');
    return 'reader'; // Default to read-only if token is missing
  }

  try {
    const decoded = jwtDecode(idToken);
    const groups = decoded['cognito:groups'];

    // Check if cognito:groups claim exists
    if (!groups) {
      console.warn('parseUserRole: cognito:groups claim not found in token, defaulting to reader');
      return 'reader'; // Default role if no groups claim exists
    }

    if (!Array.isArray(groups)) {
      console.warn('parseUserRole: cognito:groups claim is not an array, defaulting to reader');
      return 'reader'; // Default role if groups is not an array
    }

    // Check for librarian role first (higher privilege)
    // Check for both 'KMSDID-Librarians' and 'librarians' for backwards compatibility
    if (groups.includes('KMSDID-Librarians') || groups.includes('librarians')) {
      return 'librarian';
    }

    return 'reader';
  } catch (error) {
    console.error('parseUserRole: Error decoding JWT token:', error);
    return 'reader'; // Default to read-only on error
  }
};

/**
 * Check if user has librarian access
 * @param {string} idToken - JWT ID token from Cognito
 * @returns {boolean} - True if user has librarian access
 */
export const hasLibrarianAccess = (idToken) => {
  const role = parseUserRole(idToken);
  return role === 'librarian';
};

/**
 * Extract username from JWT token
 * @param {string} idToken - JWT ID token from Cognito
 * @returns {string} - Username or email from token
 */
export const getUsernameFromToken = (idToken) => {
  if (!idToken || typeof idToken !== 'string') {
    return 'Unknown User';
  }

  try {
    const decoded = jwtDecode(idToken);
    // Try to get email first, then cognito:username, then 'sub' (user ID)
    return decoded.email || decoded['cognito:username'] || decoded.sub || 'Unknown User';
  } catch (error) {
    console.error('getUsernameFromToken: Error decoding JWT token:', error);
    return 'Unknown User';
  }
};
